import React, { useMemo, useState, useEffect, ChangeEvent } from 'react';
import type { ContactResponse } from '@troia-v3/types';
import { Drawer } from '@/shared/components/ui/Drawer';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Textarea } from '@/shared/components/ui/Textarea';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import { useCustomersDropdown } from '@/modules/crm/customers/hooks';
import { DevicesTable, type DeviceRow } from '@/shared/components/devices/DevicesTable';
import { useContactDevices } from '../hooks/useContacts';
import TagInput from './TagInput';
import { ProviderIdentifiersDisplay } from './ProviderIdentifiersDisplay';
import { Icon } from '@/shared/icons'
interface EditContactDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (id: string, data: EditContactFormData, avatar?: File) => void;
  contact: ContactResponse | null;
  isLoading?: boolean;
}

export interface EditContactFormData {
  name: string;
  identifiers: {
    email: string[];
    phone: string[];
  };
  tags?: string[];
  notes?: string;
  /** Vínculo com `customers` (Plano C). String vazia = "— Sem cliente —" (limpa o vínculo). */
  customerId?: string;
  personalInfo?: {
    birthDate?: string;
    /** CPF, CNPJ ou outro documento — texto livre. */
    document?: string;
    address?: {
      street?: string;
      number?: string;
      complement?: string;
      neighborhood?: string;
      city?: string;
      state?: string;
      zipCode?: string;
      country?: string;
    };
  };
}

/**
 * Drawer para editar contato existente
 *
 * Suporta múltiplos emails, telefones e tags
 * Exibe identificadores de providers (read-only)
 * Upload opcional de avatar
 * Abas: Informações de Contato + Informações Pessoais
 * Validação: pelo menos 1 email OU 1 telefone obrigatório
 *
 * @example
 * <EditContactDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSave={handleUpdateContact}
 *   contact={selectedContact}
 * />
 */
const EditContactDialog: React.FC<EditContactDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  contact,
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<'contact' | 'personal' | 'devices'>('contact');
  const [formData, setFormData] = useState<EditContactFormData>({
    name: '',
    identifiers: {
      email: [],
      phone: []
    },
    tags: [],
    notes: '',
    customerId: '',
    personalInfo: {
      address: {
        country: 'Brasil',
      },
    },
  });

  // Dropdown de clientes (Plano C) — drawer só fica montado com `isOpen`,
  // hook gated explicitamente (CLAUDE.md SEMPRE #10).
  const { data: customersDropdown, isLoading: isLoadingCustomers } = useCustomersDropdown({ enabled: isOpen });

  // Dispositivos só são buscados quando a aba está aberta (hooks rodam antes do
  // `if (!isOpen) return null` — regra de fetch em drawers).
  const devicesTabActive = isOpen && activeTab === 'devices';
  const {
    data: contactDevices,
    isLoading: isLoadingDevices,
    isError: isDevicesError,
  } = useContactDevices(contact?.id, devicesTabActive);
  // Nome do canal para a coluna Origem — rota dropdown (sem permissão de módulo).
  const { data: channelsDropdown } = useChannelsDropdown({ enabled: devicesTabActive });

  const deviceRows: DeviceRow[] = useMemo(() => {
    const channelNameById = new Map((channelsDropdown ?? []).map((channel) => [channel.id, channel.name]));

    return (contactDevices ?? []).map((device) => ({
      ...device,
      origin: device.origins
        .map((origin) => {
          if (origin.type === 'website') return 'Site';
          const channelName = origin.channelId ? channelNameById.get(origin.channelId) : undefined;
          return channelName ? `Widget · ${channelName}` : 'Widget';
        })
        .join(' · '),
      badges: device.pwaInstalledAt ? [{ text: 'App instalado', color: '#15803d', bg: '#dcfce7' }] : [],
    }));
  }, [contactDevices, channelsDropdown]);
  const customerOptions = useMemo(
    () => [
      { value: '', label: isLoadingCustomers ? 'Carregando...' : '— Sem cliente —' },
      ...(customersDropdown ?? []).map((c) => ({ value: c.id, label: c.name })),
    ],
    [customersDropdown, isLoadingCustomers]
  );

  const [avatar, setAvatar] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [errors, setErrors] = useState<{
    name?: string;
    avatar?: string;
  }>({});

  // Preencher formulário quando contato mudar
  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        identifiers: {
          email: contact.identifiers?.email || [],
          phone: contact.identifiers?.phone || []
        },
        tags: contact.tags || [],
        notes: contact.notes || '',
        customerId: contact.customerId || '',
        personalInfo: {
          birthDate: contact.personalInfo?.birthDate || '',
          document: contact.personalInfo?.document || '',
          address: {
            street: contact.personalInfo?.address?.street || '',
            number: contact.personalInfo?.address?.number || '',
            complement: contact.personalInfo?.address?.complement || '',
            neighborhood: contact.personalInfo?.address?.neighborhood || '',
            city: contact.personalInfo?.address?.city || '',
            state: contact.personalInfo?.address?.state || '',
            zipCode: contact.personalInfo?.address?.zipCode || '',
            country: contact.personalInfo?.address?.country || 'Brasil',
          },
        },
      });

      // Carregar avatar existente
      if (contact.picture) {
        setAvatarPreview(contact.picture);
      } else {
        setAvatarPreview('');
      }
      setAvatar(null);
      setActiveTab('contact');
    }
  }, [contact]);

  const handleNameChange = (value: string) => {
    setFormData((prev) => ({ ...prev, name: value }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleEmailsChange = (emails: string[]) => {
    setFormData((prev) => ({
      ...prev,
      identifiers: { ...prev.identifiers, email: emails }
    }));
  };

  const handlePhonesChange = (phones: string[]) => {
    setFormData((prev) => ({
      ...prev,
      identifiers: { ...prev.identifiers, phone: phones }
    }));
  };

  const handleTagsChange = (tags: string[]) => {
    setFormData((prev) => ({ ...prev, tags }));
  };

  const handleCustomerIdChange = (value: string) => {
    setFormData((prev) => ({ ...prev, customerId: value }));
  };

  const handleNotesChange = (value: string) => {
    setFormData((prev) => ({ ...prev, notes: value }));
  };

  const handlePersonalInfoChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value,
      },
    }));
  };

  const handleAddressChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        address: {
          ...prev.personalInfo?.address,
          [field]: value,
        },
      },
    }));
  };

  const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, avatar: 'Por favor, selecione apenas arquivos de imagem' }));
      return;
    }

    // Validar tamanho (5MB máximo)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setErrors((prev) => ({ ...prev, avatar: 'Arquivo muito grande. O tamanho máximo é 5MB.' }));
      return;
    }

    // Limpar erro anterior
    setErrors((prev) => ({ ...prev, avatar: undefined }));

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setAvatar(file);
  };

  const handleRemoveAvatar = () => {
    setAvatar(null);
    setAvatarPreview('');
    setErrors((prev) => ({ ...prev, avatar: undefined }));
  };

  const validateEmail = (email: string): string | null => {
    if (!email.trim()) {
      return 'Email não pode estar vazio';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Email inválido';
    }
    return null;
  };

  const validatePhone = (phone: string): string | null => {
    if (!phone.trim()) {
      return 'Telefone não pode estar vazio';
    }
    if (!/^[\d\s\-\+\(\)]+$/.test(phone)) {
      return 'Formato de telefone inválido';
    }
    return null;
  };

  const validateForm = (): boolean => {
    const newErrors: {
      name?: string;
      avatar?: string;
    } = {};

    // Nome obrigatório
    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !contact) {
      return;
    }

    // Filtrar valores vazios dos arrays
    const cleanedData: EditContactFormData = {
      ...formData,
      identifiers: {
        email: formData.identifiers.email.filter(e => e.trim() !== ''),
        phone: formData.identifiers.phone.filter(p => p.trim() !== '')
      },
      tags: formData.tags?.filter(t => t.trim() !== '')
    };

    onSave(contact.id, cleanedData, avatar || undefined);
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      name: '',
      identifiers: {
        email: [],
        phone: []
      },
      tags: [],
      notes: '',
      customerId: '',
      personalInfo: {
        address: {
          country: 'Brasil',
        },
      },
    });
    setAvatar(null);
    setAvatarPreview('');
    setErrors({});
    setActiveTab('contact');
    onClose();
  };

  const footerContent = (
    <div className="flex items-center justify-end gap-3 w-full">
      <Button
        type="button"
        variant="secondary"
        onClick={handleClose}
        disabled={isLoading}
        data-testid="edit-contact-cancel"
      >
        Cancelar
      </Button>
      <Button
        type="submit"
        form="edit-contact-form"
        variant="primary"
        loading={isLoading}
        disabled={isLoading}
        data-testid="edit-contact-submit"
      >
        {isLoading ? 'Salvando...' : 'Salvar Alterações'}
      </Button>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      // `icon` recebe o ícone CRU — o Drawer já o encaixota no chip roxo do
      // `.drw-head`; encaixotar aqui produzia chip dentro de chip.
      icon={<Icon name="edit" className="w-5 h-5" />}
      title="Editar Contato"
      tabs={{
        items: [
          { id: 'contact', label: 'Informações de Contato' },
          { id: 'personal', label: 'Informações Pessoais' },
          { id: 'devices', label: 'Dispositivos' },
        ],
        active: activeTab,
        onChange: (id) => setActiveTab(id as 'contact' | 'personal' | 'devices'),
      }}
      footer={footerContent}
      disabled={isLoading}
    >
      <div className="px-6 py-5">
      <form id="edit-contact-form" onSubmit={handleSubmit}>
        {/* Tab: Informações de Contato */}
        {activeTab === 'contact' && (
          <div className="space-y-4">
            {/* Avatar Upload */}
            <div>
              <label
                className="block text-sm font-medium mb-2"
                style={{ color: '#1e293b' }}
              >
                Foto do Contato
              </label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {avatarPreview ? (
                    <div className="relative">
                      <img
                        src={avatarPreview}
                        alt="Avatar preview"
                        className="w-20 h-20 rounded-full object-cover border-2"
                        style={{ borderColor: '#e5e7eb' }}
                      />
                      <button
                        type="button"
                        onClick={handleRemoveAvatar}
                        className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        disabled={isLoading}
                      >
                        <Icon name="x" className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center border-2"
                      style={{
                        backgroundColor: '#f1f5f9',
                        borderColor: '#e5e7eb',
                      }}
                    >
                      <Icon name="user" className="w-8 h-8" style={{ color: '#64748b' }} />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    id="avatar-upload-edit"
                    accept="image/*"
                    onChange={handleAvatarChange}
                    className="hidden"
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="avatar-upload-edit"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg cursor-pointer disabled:opacity-50 transition-colors"
                    style={{
                      color: '#1e293b',
                      backgroundColor: '#ffffff',
                      borderColor: '#e5e7eb',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                  >
                    <Icon name="image" className="w-4 h-4" />
                    {avatarPreview ? 'Alterar Foto' : 'Selecionar Foto'}
                  </label>
                  <p
                    className="mt-1 text-xs"
                    style={{ color: '#64748b' }}
                  >
                    JPG, PNG ou GIF (max. 5MB)
                  </p>
                </div>
              </div>
              {errors.avatar && (
                <p className="mt-1 text-sm text-red-600">{errors.avatar}</p>
              )}
            </div>

            {/* Nome */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Nome <span className="text-red-500">*</span>
              </label>
              <Input
                icon={'user'}
                iconPosition="left"
                type="text"
                value={formData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Nome completo"
                error={errors.name}
                disabled={isLoading}
                data-testid="edit-contact-name-input"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            {/* Cliente (Plano C — vínculo com customers) */}
            <Select
              label="Cliente"
              options={customerOptions}
              value={formData.customerId || ''}
              onChange={handleCustomerIdChange}
              disabled={isLoading}
              data-testid="contact-customer-select"
            />

            {/* Emails (Tag Input) */}
            <TagInput
              label="Emails"
              values={formData.identifiers.email}
              onChange={handleEmailsChange}
              placeholder="Digite um email e pressione Enter"
              icon={<Icon name="mail" className="w-4 h-4 text-gray-400" />}
              type="email"
              validation={validateEmail}
              disabled={isLoading}
              maxItems={5}
              data-testid="edit-contact-emails"
            />

            {/* Telefones (Tag Input) */}
            <TagInput
              label="Telefones"
              values={formData.identifiers.phone}
              onChange={handlePhonesChange}
              placeholder="Digite um telefone e pressione Enter"
              icon={<Icon name="phone" className="w-4 h-4 text-gray-400" />}
              type="tel"
              validation={validatePhone}
              disabled={isLoading}
              maxItems={5}
              data-testid="edit-contact-phones"
            />

            {/* Provider Identifiers (Read-Only) */}
            {contact && <ProviderIdentifiersDisplay identifiers={contact.identifiers} />}

            {/* Tags (Tag Input) */}
            <TagInput
              label="Tags"
              values={formData.tags || []}
              onChange={handleTagsChange}
              placeholder="Digite uma tag e pressione Enter"
              icon={<Icon name="tag" className="w-4 h-4 text-gray-400" />}
              type="text"
              disabled={isLoading}
              maxItems={10}
              data-testid="edit-contact-tags"
            />

            {/* Notas */}
            <Textarea
              label="Notas"
              value={formData.notes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Adicione notas..."
              rows={3}
              resize="none"
              disabled={isLoading}
            />
          </div>
        )}

        {/* Tab: Informações Pessoais */}
        {activeTab === 'personal' && (
          <div className="space-y-4">
            {/* Data de Nascimento */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Data de Nascimento
              </label>
              <Input
                icon={'calendar'}
                iconPosition="left"
                type="date"
                value={formData.personalInfo?.birthDate || ''}
                onChange={(e) => handlePersonalInfoChange('birthDate', e.target.value)}
                disabled={isLoading}
              />
            </div>

            {/* Documento — CPF, CNPJ ou outro, texto livre */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Documento (CPF/CNPJ)
              </label>
              <Input
                icon={'file-text'}
                iconPosition="left"
                type="text"
                value={formData.personalInfo?.document || ''}
                onChange={(e) => handlePersonalInfoChange('document', e.target.value)}
                placeholder="CPF, CNPJ ou outro documento"
                maxLength={40}
                disabled={isLoading}
                data-testid="edit-contact-document-input"
              />
            </div>

            {/* Endereço - Título da seção */}
            <div className="flex items-center gap-2 pt-2">
              <Icon name="map-pin" className="w-4 h-4" style={{ color: '#64748b' }} />
              <span className="text-sm font-medium" style={{ color: '#1e293b' }}>
                Endereço
              </span>
            </div>

            {/* CEP */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                CEP
              </label>
              <Input
                type="text"
                value={formData.personalInfo?.address?.zipCode || ''}
                onChange={(e) => handleAddressChange('zipCode', e.target.value)}
                placeholder="00000-000"
                disabled={isLoading}
              />
            </div>

            {/* Rua + Número */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Endereço
                </label>
                <Input
                  type="text"
                  value={formData.personalInfo?.address?.street || ''}
                  onChange={(e) => handleAddressChange('street', e.target.value)}
                  placeholder="Rua / Logradouro"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Número
                </label>
                <Input
                  type="text"
                  value={formData.personalInfo?.address?.number || ''}
                  onChange={(e) => handleAddressChange('number', e.target.value)}
                  placeholder="Nº"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Complemento */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Complemento
              </label>
              <Input
                type="text"
                value={formData.personalInfo?.address?.complement || ''}
                onChange={(e) => handleAddressChange('complement', e.target.value)}
                placeholder="Apto, Sala, Bloco..."
                disabled={isLoading}
              />
            </div>

            {/* Bairro */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                Bairro
              </label>
              <Input
                type="text"
                value={formData.personalInfo?.address?.neighborhood || ''}
                onChange={(e) => handleAddressChange('neighborhood', e.target.value)}
                placeholder="Bairro"
                disabled={isLoading}
              />
            </div>

            {/* Cidade + Estado */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Cidade
                </label>
                <Input
                  type="text"
                  value={formData.personalInfo?.address?.city || ''}
                  onChange={(e) => handleAddressChange('city', e.target.value)}
                  placeholder="Cidade"
                  disabled={isLoading}
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: '#1e293b' }}
                >
                  Estado
                </label>
                <Input
                  type="text"
                  value={formData.personalInfo?.address?.state || ''}
                  onChange={(e) => handleAddressChange('state', e.target.value)}
                  placeholder="Estado"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* País */}
            <div>
              <label
                className="block text-sm font-medium mb-1"
                style={{ color: '#1e293b' }}
              >
                País
              </label>
              <Input
                type="text"
                value={formData.personalInfo?.address?.country || ''}
                onChange={(e) => handleAddressChange('country', e.target.value)}
                placeholder="País"
                disabled={isLoading}
              />
            </div>
          </div>
        )}
        {/* Tab: Dispositivos — read-only, alimentada por eventos do widget */}
        {activeTab === 'devices' && (
          <div className="space-y-3" data-testid="edit-contact-devices-tab">
            <p className="text-sm" style={{ color: '#64748b' }}>
              Aparelhos deste contato que instalaram o app do atendimento ou autorizaram
              notificações. O registro acontece nesses momentos — não em toda visita.
            </p>
            <DevicesTable
              devices={deviceRows}
              isLoading={isLoadingDevices}
              isError={isDevicesError}
              emptyMessage="Nenhum dispositivo registrado para este contato."
              showOrigin
              testId="contact-devices"
            />
          </div>
        )}
      </form>
      </div>
    </Drawer>
  );
};

export default EditContactDialog;
